import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "../../services/api";

const MATIERES = [
  { value: "quoran", label: "Qour'an" },
  { value: "arabe", label: "Langue arabe" },
  { value: "science", label: "Sciences de la religion" },
];

const fieldStyle = {
  width: "100%", height: "44px", padding: "0 14px",
  borderRadius: "8px", border: "1px solid #d2d6da",
  fontSize: "14px", color: "#344767", outline: "none",
  background: "#fff", boxSizing: "border-box",
  transition: "border-color 0.2s", fontFamily: "inherit",
};

function FieldLabel({ label, required, children }) {
  return (
    <MDBox mb={2}>
      <MDTypography variant="caption" fontWeight="bold" color="text" sx={{
        mb: 0.5, display: "block", textTransform: "uppercase",
        fontSize: 11, letterSpacing: "0.05em",
      }}>
        {label}{required && " *"}
      </MDTypography>
      {children}
    </MDBox>
  );
}

function LivreForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id && id !== "nouveau");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ nom: "", matiere: "" });

  // Photo couverture
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [showWebcam, setShowWebcam] = useState(false);
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [stream, setStream] = useState(null);

  useEffect(() => {
    if (isEdit) {
      api.get(`/livres/${id}`).then(({ data }) => {
        setForm({ nom: data.nom, matiere: data.matiere });
        if (data.couverture) setCoverPreview(`${data.couverture}`);
      });
    }
  }, [id, isEdit]);

  const startWebcam = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true });
      setStream(s);
      setShowWebcam(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = s; }, 100);
    } catch { alert("Impossible d'accéder à la webcam."); }
  };

  const stopWebcam = () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setShowWebcam(false);
    setStream(null);
  };

  const takePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      const file = new File([blob], "couverture.jpg", { type: "image/jpeg" });
      setCoverFile(file);
      setCoverPreview(canvas.toDataURL("image/jpeg"));
    }, "image/jpeg");
    stopWebcam();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverFile(file);
      setCoverPreview(URL.createObjectURL(file));
    }
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) {
      setError("Le nom du livre est obligatoire");
      return;
    }
    if (!form.matiere) {
      setError("La matière est obligatoire");
      return;
    }
    setError("");
    setSaving(true);
    try {
      // Utiliser FormData pour envoyer la couverture
      const fd = new FormData();
      fd.append("nom", form.nom);
      fd.append("matiere", form.matiere);
      if (coverFile) fd.append("couverture", coverFile);

      if (isEdit) {
        await api.put(`/livres/${id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post("/livres", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      navigate("/livres");
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={7}>

            <MDBox mb={3} display="flex" alignItems="center" gap={2}>
              <MDButton variant="outlined" color="secondary" size="small"
                onClick={() => navigate("/livres")}
                sx={{ borderRadius: "10px", px: 2, whiteSpace: "nowrap" }}>
                <Icon sx={{ mr: 0.5 }}>arrow_back</Icon> Retour à la liste
              </MDButton>
              <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#344767" }}>
                {isEdit ? "Modifier le livre" : "Ajouter un livre"}
              </MDTypography>
            </MDBox>

            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
              <MDBox mx={2} mt={-3} py={2} px={3} sx={{
                background: "linear-gradient(135deg, #e65100 0%, #f57c00 100%)",
                borderRadius: "12px", boxShadow: "0 4px 20px rgba(230,81,0,0.4)",
              }}>
                <MDTypography variant="h6" color="white" fontWeight="bold">
                  {isEdit ? "Modifier le livre" : "Nouveau livre"}
                </MDTypography>
              </MDBox>

              <MDBox pt={4} pb={4} px={4}>
                {error && (
                  <MDBox mb={3} p={2}
                    sx={{ background: "#ffebee", borderRadius: "10px", border: "1px solid #ffcdd2" }}>
                    <MDTypography variant="caption" color="error" fontWeight="medium">
                      ⚠️ {error}
                    </MDTypography>
                  </MDBox>
                )}

                <MDBox component="form" onSubmit={handleSubmit} autoComplete="off">
                  <Grid container spacing={3}>

                    {/* ── COUVERTURE ── */}
                    <Grid item xs={12} md={4}>
                      <FieldLabel label="Couverture du livre">
                        <MDBox
                          sx={{
                            width: "100%", aspectRatio: "3/4",
                            borderRadius: "12px", border: "2px dashed #d2d6da",
                            overflow: "hidden", background: "#f8f8f8",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            position: "relative",
                          }}
                        >
                          {coverPreview ? (
                            <img src={coverPreview} alt="couverture"
                              style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            <MDBox display="flex" flexDirection="column" alignItems="center" gap={1}>
                              <Icon sx={{ fontSize: 40, color: "#e0e0e0" }}>menu_book</Icon>
                              <MDTypography variant="caption" color="text">
                                Aucune couverture
                              </MDTypography>
                            </MDBox>
                          )}
                        </MDBox>
                        <MDBox display="flex" flexDirection="column" gap={1} mt={1}>
                          <MDButton variant="outlined" size="small" component="label"
                            sx={{ color: "#e65100", borderColor: "#e65100" }}>
                            <Icon>upload</Icon>&nbsp; Uploader
                            <input type="file" accept="image/*" hidden onChange={handleFileUpload} />
                          </MDButton>
                          <MDButton variant="outlined" color="secondary" size="small"
                            onClick={startWebcam}>
                            <Icon>camera_alt</Icon>&nbsp; Webcam
                          </MDButton>
                          {coverPreview && (
                            <MDButton variant="outlined" color="error" size="small"
                              onClick={() => { setCoverPreview(null); setCoverFile(null); }}>
                              <Icon>cancel</Icon>&nbsp; Supprimer
                            </MDButton>
                          )}
                        </MDBox>
                      </FieldLabel>

                      {/* Webcam */}
                      {showWebcam && (
                        <MDBox mt={2}>
                          <video ref={videoRef} autoPlay
                            style={{ width: "100%", borderRadius: 10 }} />
                          <canvas ref={canvasRef} style={{ display: "none" }} />
                          <MDBox display="flex" gap={1} mt={1}>
                            <MDButton variant="gradient" color="success" size="small"
                              onClick={takePhoto}>
                              <Icon>camera</Icon>&nbsp; Prendre
                            </MDButton>
                            <MDButton variant="outlined" color="error" size="small"
                              onClick={stopWebcam}>
                              Annuler
                            </MDButton>
                          </MDBox>
                        </MDBox>
                      )}
                    </Grid>

                    {/* ── INFORMATIONS ── */}
                    <Grid item xs={12} md={8}>
                      <FieldLabel label="Nom du livre *">
                        <input
                          name="nom" value={form.nom} onChange={handleChange}
                          placeholder="Al Moukhtasar..."
                          autoComplete="off" style={fieldStyle}
                          onFocus={(e) => (e.target.style.borderColor = "#e65100")}
                          onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
                        />
                      </FieldLabel>

                      <FieldLabel label="Matière *">
                        <select
                          name="matiere" value={form.matiere} onChange={handleChange}
                          style={{ ...fieldStyle, color: form.matiere ? "#344767" : "#9ca3af" }}
                          onFocus={(e) => (e.target.style.borderColor = "#e65100")}
                          onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
                        >
                          <option value="">— Choisir une matière —</option>
                          {MATIERES.map((m) => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                          ))}
                        </select>
                      </FieldLabel>
                    </Grid>
                  </Grid>

                  <MDBox display="flex" gap={2} justifyContent="flex-end" mt={3}>
                    <MDButton variant="outlined" color="secondary"
                      onClick={() => navigate("/livres")}>
                      Annuler
                    </MDButton>
                    <MDButton type="submit" disabled={saving} sx={{
                      px: 4, background: "linear-gradient(135deg, #e65100, #f57c00)",
                      color: "#fff", borderRadius: "8px", fontWeight: 700,
                      "&:hover": { background: "#bf360c" },
                    }}>
                      {saving ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Créer le livre"}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default LivreForm;